/**
 * The mansard: the app's sloped ceiling, primitive 1495 (`AddAtticHelper.AtticFurnitureId`,
 * `ModelSettings.DefaultSloppedCeilingId`). It is a wedge under the ceiling along a wall — its back on the
 * wall's face, its top against the ceiling, its slope running from the wall at the knee up to the ceiling
 * across its depth — and a ceiling item like any other in the project: the model 1495, its pivot at the top
 * of its back edge in the middle of its width (the app places it at the wall side's `Top3`), its size in
 * Scale. The app adds it along a wall side (`AddAtticCommand`): as wide as the side, 1 m deep and 1 m high,
 * then opens its resize.
 *
 * Windows in a mansard are the wall's own openings, as the app keeps them, and the wedge makes room for them
 * (the app does not: its wedge simply stands in front of whatever is in the wall behind it):
 *   - under the knee a window stays in the knee wall;
 *   - one that reaches above the knee stays upright in the wall, and the slope in front of it is cut back into
 *     a niche to its top (a dormer, «слуховое окно»);
 *   - a window wholly above the knee and within the wedge's width lies in the slope (a roof window,
 *     «мансардное окно»): 3D turns it into the slope over the same heights, so the wall plan, which draws the wall
 *     face-on, shows it where it is, and the niche behind it opens onto the wall's own hole and the view.
 * Nothing of this enters the project: an opening is still where the app has it, and the cut and the turn are
 * worked out every time from the wedge and the openings of its wall.
 */
import type { Item, Level, Opening, Vec2, Wall } from '../core/hp/types';
import { contains } from '../core/geometry/edit';
import { buildWallSolids } from '../core/geometry/walls';
import { openingKind } from './openingProperties';
import { cachedModelTags } from './modelTags';
import { ATTIC_MODEL, isAttic } from '../core/geometry/attic';
import { t } from '../i18n/I18n';

export { ATTIC_MODEL, isAttic };

/** The sloped ceiling's catalog tags. */
export const ATTIC_TAGS = ['Primitives', 'RoomObjects', 'SloppedCeiling', 'FullDragOnPlanes', 'Wall', 'Floor', 'Ceiling'];
/** 100 × 100 × 100 cm in the catalog: a new one is 1 m high and deep, and as wide as its wall's face. */
export const ATTIC_DEFAULT = { height: 1, depth: 1 };
/** `AddAtticHelper.HasEnoughLength`: a wall face shorter than this takes no mansard. */
export const ATTIC_MIN_FACE = 1;

/** How close to a wall's face the back of a wedge has to be to stand on it. */
const ON_FACE = .03;
const round = (value: number) => Math.round(value * 1e4) / 1e4;
/** The yaw that turns an item's front (its local +z) to `normal`. */
const facing = (normal: Vec2) => Math.round(Math.atan2(-normal.x, normal.z) * 1e5) / 1e5;

/** A wedge in world terms. Its own coordinates: `a` across its width from its middle, `n` into the room from its back, `y` up from the knee. */
export interface AtticFrame {
  /** The top of its back edge, in the middle of its width: the item's position (the app's `Top3`). */
  origin: Vec2;
  /** Across its width (the item's local +x), and into the room. */
  right: Vec2;
  inward: Vec2;
  /** 1 when the depth runs along the item's local +z, as the model's does; −1 for one a project turned round. */
  zSign: 1 | -1;
  width: number;
  height: number;
  depth: number;
  /** The heights of its back edge: the knee, where the slope meets the wall, and the top against the ceiling. */
  knee: number;
  top: number;
  /** The wall whose face its back stands on, and which face: 1 the room's (`normalIn`), −1 the other. */
  wall?: Wall;
  side?: 1 | -1;
}

/**
 * The wedge `item` makes. Its back is on a wall's face when its position lies on that face (3 cm) within the wall's
 * length, square to it: then its depth runs away from that wall, whichever way the project turned the model — a
 * mansard never stands out through its wall.
 */
export function atticFrame(item: Item, walls: readonly Wall[]): AtticFrame {
  const c = Math.cos(item.rotation), s = Math.sin(item.rotation);
  const right = { x: c, z: s }, forward = { x: -s, z: c };
  const frame: AtticFrame = { origin: item.position, right, inward: forward, zSign: 1, width: item.size.w, height: item.size.h, depth: item.size.d,
    knee: item.elevation, top: item.elevation + item.size.h };
  let best = ON_FACE;
  for (const wall of walls) {
    if (wall.hidden) continue;
    const n = wall.normalIn, square = forward.x * n.x + forward.z * n.z;
    if (Math.abs(square) < .98) continue;
    const dx = item.position.x - wall.center.x, dz = item.position.z - wall.center.z;
    if (Math.abs(dx * wall.dir.x + dz * wall.dir.z) > wall.length / 2 + .05) continue;
    const across = dx * n.x + dz * n.z;
    for (const side of [1, -1] as const) {
      const off = Math.abs(across - side * wall.thickness / 2);
      if (off >= best) continue;
      best = off;
      frame.wall = wall; frame.side = side;
      frame.inward = { x: n.x * side, z: n.z * side };
      frame.zSign = forward.x * frame.inward.x + forward.z * frame.inward.z < 0 ? -1 : 1;
    }
  }
  return frame;
}

/** A point of the wedge, in its own coordinates, on the plan. */
export function atticPoint(frame: AtticFrame, a: number, n: number): Vec2 {
  return { x: frame.origin.x + frame.right.x * a + frame.inward.x * n, z: frame.origin.z + frame.right.z * a + frame.inward.z * n };
}

/** How far into the room the slope is at `y` over the knee. */
export const slopeDepthAt = (frame: Pick<AtticFrame, 'height' | 'depth'>, y: number): number => frame.height > 0 ? Math.max(0, Math.min(1, y / frame.height)) * frame.depth : 0;

/** The level a wall's face is measured in: its walls' mitred corners come from the whole level. */
type FaceLevel = Pick<Level, 'walls' | 'openings' | 'rooms' | 'height'>;

/** The room a step in front of this face of the wall is in, if any: the smallest, as the wall plan finds it. */
function roomInFront(level: Pick<Level, 'rooms'>, wall: Wall, side: 1 | -1, along = wall.length / 2) {
  const reach = wall.thickness / 2 + .05, n = { x: wall.normalIn.x * side, z: wall.normalIn.z * side };
  const point = { x: wall.start.x + wall.dir.x * along + n.x * reach, z: wall.start.z + wall.dir.z * along + n.z * reach };
  return level.rooms.filter(room => room.polygon.length >= 3 && contains(room.polygon, point)).sort((a, b) => a.area - b.area)[0];
}

/**
 * The stretch this face of the wall has, along the wall from its start: between the mitred corners of that side (a
 * room's outline runs along its walls' middles, the face stops short of it at every corner), and the ceiling's
 * height over it, the room's in front of it or else the wall's.
 */
export function wallFace(level: FaceLevel, wall: Wall, side: 1 | -1): { from: number; to: number; height: number } {
  const solid = buildWallSolids(level as Level).find(value => value.wall.id === wall.id);
  const along = (p: Vec2) => (p.x - wall.start.x) * wall.dir.x + (p.z - wall.start.z) * wall.dir.z;
  let from = 0, to = wall.length;
  if (solid) {
    // `normalIn` is the solid's left side when `interiorIsLeft`; the other face is the other side.
    const left = (side === 1) === solid.interiorIsLeft, c = solid.corners;
    const a = along(left ? c.leftStart : c.rightStart), b = along(left ? c.leftEnd : c.rightEnd);
    from = Math.max(0, Math.min(a, b)); to = Math.min(wall.length, Math.max(a, b));
  }
  const room = roomInFront(level, wall, side, (from + to) / 2);
  return { from, to, height: room?.height ?? wall.height };
}

/** The mansards standing on this face of the wall. */
export function atticsOn(level: Pick<Level, 'items' | 'walls'>, wall: Wall, side: 1 | -1): Item[] {
  return level.items.filter(item => { if (!isAttic(item)) return false; const frame = atticFrame(item, level.walls); return frame.wall?.id === wall.id && frame.side === side; });
}

/**
 * Whether «Добавить мансарду» belongs on this face of the wall (`AddAtticHelper.CanShowButtonOnWallSide`): a face
 * longer than a metre, with no mansard on it yet and no ceiling molding.
 */
export function takesAttic(level: FaceLevel & Pick<Level, 'items'>, wall: Wall, side: 1 | -1): boolean {
  if (wall.hidden || wall.molding || !roomInFront(level, wall, side)) return false;
  const face = wallFace(level, wall, side);
  return face.to - face.from > ATTIC_MIN_FACE && atticsOn(level, wall, side).length === 0;
}

/**
 * A mansard along this face of the wall, as `AddAtticCommand` makes one: its back at the top of the face in the
 * face's middle, as wide as the face, 1 m deep and 1 m high under the ceiling, turned into the room.
 */
export function newAttic(level: FaceLevel, wall: Wall, side: 1 | -1, id: string): Item {
  const face = wallFace(level, wall, side), n = { x: wall.normalIn.x * side, z: wall.normalIn.z * side };
  const middle = (face.from + face.to) / 2, width = round(face.to - face.from);
  const ceiling = face.height > 0 ? face.height : wall.height, height = Math.min(ATTIC_DEFAULT.height, ceiling);
  return {
    id, modelRef: { kind: 'local', id: ATTIC_MODEL, raw: 'Models/' + ATTIC_MODEL }, caption: t('#AtticSlopedCeiling'), tags: [...ATTIC_TAGS],
    placement: 'ceiling',
    position: { x: round(wall.start.x + wall.dir.x * middle + n.x * wall.thickness / 2), z: round(wall.start.z + wall.dir.z * middle + n.z * wall.thickness / 2) },
    elevation: round(ceiling - height), rotation: facing(n), size: { w: width, h: height, d: ATTIC_DEFAULT.depth },
    scale: [width, height, ATTIC_DEFAULT.depth], mirrored: false, pivot: 'bottom', usesExplicitTiling: true,
  };
}

/** The face of a wall of this room nearest to `point` that takes a mansard: the wall a tap on the ceiling plan was nearest to. */
export function nearestAtticWall(level: FaceLevel & Pick<Level, 'items'>, roomIndex: number, point: Vec2 | null): { wall: Wall; side: 1 | -1 } | null {
  const room = level.rooms.find(value => value.index === roomIndex);
  if (!room || room.polygon.length < 3) return null;
  const at = point ?? room.polygon.reduce((sum, v) => ({ x: sum.x + v.x / room.polygon.length, z: sum.z + v.z / room.polygon.length }), { x: 0, z: 0 });
  let best: { wall: Wall; side: 1 | -1; distance: number } | null = null;
  for (const wall of level.walls) {
    for (const side of [1, -1] as const) {
      if (roomInFront(level, wall, side)?.index !== roomIndex || !takesAttic(level, wall, side)) continue;
      const face = wallFace(level, wall, side), n = { x: wall.normalIn.x * side, z: wall.normalIn.z * side };
      const along = Math.max(face.from, Math.min(face.to, (at.x - wall.start.x) * wall.dir.x + (at.z - wall.start.z) * wall.dir.z));
      const near = { x: wall.start.x + wall.dir.x * along + n.x * wall.thickness / 2, z: wall.start.z + wall.dir.z * along + n.z * wall.thickness / 2 };
      const distance = Math.hypot(near.x - at.x, near.z - at.z);
      if (!best || distance < best.distance) best = { wall, side, distance };
    }
  }
  return best && { wall: best.wall, side: best.side };
}

/**
 * Where an opening of the wedge's wall goes through the wedge: across its width `a0`–`a1` and up from the knee
 * `y0`–`y1`. `roof` for a window lying in the slope; otherwise the wedge is cut back to the opening's top in front
 * of it, a dormer.
 */
export interface AtticCut { openingId: string; a0: number; a1: number; y0: number; y1: number; roof: boolean }

/** A model's tags, when the caller knows them: a cloud window is only a window by its tags. */
export type TagsOf = (modelId: string) => readonly string[];
const knownTags: TagsOf = id => cachedModelTags(id) ?? [];
/** A window lies in the slope when its bottom is this close to the knee or over it. */
const KNEE_TOLERANCE = .005;

/** The openings of the wedge's wall that reach into it, in the wedge's own terms (see the note at the top). */
export function atticCuts(level: Pick<Level, 'walls' | 'openings'>, item: Item, frame: AtticFrame = atticFrame(item, level.walls), tagsOf: TagsOf = knownTags): AtticCut[] {
  const wall = frame.wall;
  if (!wall) return [];
  const cuts: AtticCut[] = [];
  for (const opening of level.openings) {
    if (opening.wallId !== wall.id) continue;
    const centre = { x: wall.start.x + wall.dir.x * opening.offset, z: wall.start.z + wall.dir.z * opening.offset };
    const along = (centre.x - frame.origin.x) * frame.right.x + (centre.z - frame.origin.z) * frame.right.z;
    const a0 = Math.max(along - opening.width / 2, -frame.width / 2), a1 = Math.min(along + opening.width / 2, frame.width / 2);
    const y0 = Math.max(opening.sill, frame.knee) - frame.knee, y1 = Math.min(opening.sill + opening.height, frame.top) - frame.knee;
    if (a1 - a0 < .01 || y1 - y0 < .01) continue;
    const within = along - opening.width / 2 >= -frame.width / 2 - .01 && along + opening.width / 2 <= frame.width / 2 + .01;
    const roof = within && opening.sill >= frame.knee - KNEE_TOLERANCE && openingKind(opening, tagsOf(opening.modelRef.id)) === 'window';
    cuts.push({ openingId: opening.id, a0: round(a0), a1: round(a1), y0: round(y0), y1: round(y1), roof });
  }
  return cuts;
}

/** A window lying in a mansard's slope, with the wedge it lies in and the mansard that makes it. */
export interface RoofWindow { opening: Opening; wall: Wall; frame: AtticFrame; cut: AtticCut; item: Item }

/** Every window of the level that lies in a mansard's slope, by opening id. */
export function roofWindows(level: Pick<Level, 'walls' | 'openings' | 'items'>, tagsOf: TagsOf = knownTags): Map<string, RoofWindow> {
  const out = new Map<string, RoofWindow>();
  for (const item of level.items) {
    if (!isAttic(item)) continue;
    const frame = atticFrame(item, level.walls);
    if (!frame.wall) continue;
    for (const cut of atticCuts(level, item, frame, tagsOf)) {
      if (!cut.roof || out.has(cut.openingId)) continue;
      const opening = level.openings.find(value => value.id === cut.openingId)!;
      out.set(cut.openingId, { opening, wall: frame.wall, frame, cut, item });
    }
  }
  return out;
}

/**
 * What a window has to do with a mansard, for the inspector: none, a dormer in front of it, or lying in its slope;
 * `within` when the wedge spans its whole width, so it would lie in the slope from the knee up.
 */
export function openingInAttic(level: Pick<Level, 'walls' | 'openings' | 'items'>, opening: Opening, tagsOf: TagsOf = knownTags):
  { kind: 'dormer' | 'roof'; knee: number; slope: number; within: boolean } | null {
  for (const item of level.items) {
    if (!isAttic(item)) continue;
    const frame = atticFrame(item, level.walls);
    const cut = atticCuts(level, item, frame, tagsOf).find(value => value.openingId === opening.id);
    if (!cut) continue;
    // The length along the slope of what the window covers of the wedge's height.
    const slope = Math.hypot(cut.y1 - cut.y0, slopeDepthAt(frame, cut.y1) - slopeDepthAt(frame, cut.y0));
    const within = cut.a1 - cut.a0 >= opening.width - .02;
    return { kind: cut.roof ? 'roof' : 'dormer', knee: frame.knee, slope, within };
  }
  return null;
}
